import { Box } from '@modusign/design-system';
import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useMount, useUnmount } from 'react-use';

import withAuthentication from '@app/containers/withAuthentication';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import { usePDF } from '@app/hooks/usePDF';
import usePreventUnload from '@app/hooks/usePreventUnload';
import embeddedService from '@app/libs/embedded/embeddedService';
import { clearDocumentSetupState } from '@app/store/documentSetup/actions';
import { templateClearAllState } from '@app/store/template/actions';
import { titleSelector, urlSelector } from '@app/store/template/selectors';
import { getFileFromURL } from '@app/store/transport-layer/files';
import { getUser } from '@app/store/user/selectors';

interface TemplateCreationLayoutProps {
  children: ReactNode;
}

export const TemplateCreationLayout = withAuthentication(
  ({ children }: TemplateCreationLayoutProps) => {
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const titleFromFile = useSelector(titleSelector);
    const fileUrl = useSelector(urlSelector);
    const history = useHistory();
    const { initialize } = useSigningState();
    const { setPDF, resetPDF } = usePDF();

    usePreventUnload(!embeddedService.isEmbedded);

    useMount(() => {
      if (!fileUrl) {
        if (embeddedService.isEmbedded) {
          history.replace('/template/setup/participant-setup');
        } else {
          history.replace('/templates');
        }
      }

      initializeOnMount();

      async function initializeOnMount() {
        resetPDF();
        if (fileUrl) {
          // 임베디드의 경우 pdf 없이 템플릿 에디터로 접근후 pdf를 업로드 할 수 있다
          const { data: pdf } = await getFileFromURL(fileUrl);
          setPDF(pdf);
        }

        /*
        임베디드 드래프트의 경우 saga에서 렌더트리를 set 하게 됩니다.
        아래 로직은 그 이후 실행되어 설정된 렌더트리를 초기화 하는 것을 방지하기 위해 임베디드가 아닐경우에만 초기화 하도록 합니다.
        */
        if (!embeddedService.isEmbedded) {
          const requesterId = createDataLabel();
          initialize({
            attachments: [],
            document: {
              // (@DevSDK): document를 template data 핸들링하는데 사용할지 고려하기
              // FYI: https://modusign.slack.com/archives/C04902B1JF7/p1674694203898699
              title: titleFromFile,
              pdfFile: {
                url: fileUrl,
              },
              pdfPassword: null,
            },
            fields: [],
            participants: [
              {
                type: 'REQUESTER',
                name: user.user.name,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                contact: user.user.email!,
                id: requesterId,
                dataLabel: requesterId,
              },
            ],
            verifications: [],
            ui: {
              isInOrder: true,
              insertingField: null,
              lastInsertedField: createInitialLastInsertedField(),
            },
          });
        }
      }
    });

    useUnmount(() => {
      resetPDF();
      initialize();
      // TODO(#8052): (@Juyeong-Byeon)react-signing 완전히 template에 도입 후 제거
      dispatch(clearDocumentSetupState());
      dispatch(templateClearAllState());
    });

    return (
      <Box
        bg="#484848"
        height="100%"
        overflow="hidden"
        sx={{
          '&::before': {
            content: '""',
            display: 'block',
            left: 0,
            minHeight: '200vh',
            position: 'fixed',
            top: '-50vh',
            width: '100vw',
            zIndex: -1,
          },
        }}
      >
        {children}
      </Box>
    );
  }
);
